let visitedRoutes = []

/**
 * This composable keeps track of the user's navigation history.
 * It provides the ability to get the recent query parameters for a given route visited.
 * This is currently used for the Back Button functionality in <page-wrapper /> component.
 * It improves the user experience by remembering the filters/pagination applied on listing views, such that
 * if the user clicks on the back button in the breadcrumbs on details pages, they will be redirected
 * back to the listing page, where they left off ( with the same filters applied ),
 * similar to how the browser's back button works like.
 */
export function useRouteQueryHistory() {
  const getVisitedRoute = (route) => {
    return visitedRoutes.find((visitedRoute) => {
      if (typeof route === 'object') {
        if (visitedRoute.name === route.name) {
          return visitedRoute
        }

        if (visitedRoute.path === route.path) {
          return visitedRoute
        }
      }

      return visitedRoute.path === route
    })
  }

  const applyQueryToRoute = (route) => {
    if (typeof route === 'object') {
      const { query = {} } = getVisitedRoute(route) || {}

      return {
        ...route,
        query: {
          ...(query || {}),
          ...(route.query || {})
        }
      }
    }

    return {
      path: route,
      query: getVisitedRoute(route)?.query
    }
  }

  return {
    applyQueryToRoute
  }
}

export function useRouteQueryHistorySetup(router) {
  router.beforeEach((_, from) => {
    // The purpose of this code is to preserve the route query so that
    // the back button inside <page-wrapper /> brings the user back to the last page they were on,
    // with the correct filters applied.
    // We cannot use `route.query`, because the datatable component
    // uses the imperative `window.pushState` method to add the page number to the query.
    // This is done, because the root views have `:key="$route.fullPath"` added to them, so if the query
    // is mutated, the page will end up getting rebuild.
    const search = window.location.search || {}
    const params = new URLSearchParams(search)
    const query = Object.fromEntries(params.entries())

    // If we already have an entry for this route, we will update the query.
    // We do not want to be adding duplicate entries, otherwise if details pages are using
    // the route query, we might end up removing the entry for the details view.
    const visitedRoute = visitedRoutes.find((entry) => entry.name === from.name)

    if (visitedRoute) {
      Object.assign(visitedRoute, { query })
    } else {
      visitedRoutes.push({
        name: from.name,
        path: from.path,
        query: from.query || {}
      })
    }

    // We will only keep track of the 3 most recent visited routes, as that handles the cases where
    // user followed this journey: Listing view > Create Resource > Edit resource.
    // Keeping track of higher amount of visits introduces edge cases.
    if (visitedRoutes.length > 3) {
      visitedRoutes.shift()
    }
  })

  function cleanup() {
    visitedRoutes = []
  }

  return { cleanup }
}
